import type { ElementType } from 'react'
import { useEffect, useRef, useState } from 'react'

import { IconChartHistogram, IconCoin, IconTicket } from '@tabler/icons-react'

import { useJoinAffiliateProgram } from '@/api/v1/affiliation'
import { AffiliateTermsConsent } from '@/components/common/affiliate-terms-consent'
import { LoadingButton } from '@/components/common/loading-button'
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogDismiss,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import type { AffiliateJoinSource } from '@/lib/analytics/affiliate-tracking'
import {
  trackAffiliateActivationDialogDismissed,
  trackAffiliateActivationDialogViewed,
} from '@/lib/analytics/affiliate-tracking'

import locales from './locales'

interface Highlight {
  key: string
  icon: ElementType
  color: string
  background: string
}

const HIGHLIGHTS: ReadonlyArray<Highlight> = [
  {
    key: 'commission',
    icon: IconCoin,
    color: 'text-emerald-600 dark:text-emerald-400',
    background: 'bg-emerald-500/10',
  },
  {
    key: 'coupon',
    icon: IconTicket,
    color: 'text-violet-600 dark:text-violet-400',
    background: 'bg-violet-500/10',
  },
  {
    key: 'panel',
    icon: IconChartHistogram,
    color: 'text-blue-600 dark:text-blue-400',
    background: 'bg-blue-500/10',
  },
]

interface AffiliateActivationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fired once the account is in the program — the caller decides where the
   *  new affiliate lands and what greets them there. */
  onActivated: () => void
  /**
   * Which door opened it. The one join event stays splittable by entry point,
   * which is the whole reason `AffiliateJoinSource` exists.
   */
  source?: AffiliateJoinSource
}

/**
 * What the menu entry opens for an account that has not joined yet: the terms
 * in three lines and the one button that activates them. Closing it navigates
 * nowhere — the program is an invitation here, not a destination.
 *
 * Reaching a program page by URL instead still renders the section's own
 * landing, which carries the full terms.
 */
export function AffiliateActivationDialog({
  open,
  onOpenChange,
  onActivated,
  source = 'activation-dialog',
}: AffiliateActivationDialogProps) {
  const { t } = useComponentTranslation(locales)
  // No success toast: the welcome modal that follows is the acknowledgement,
  // and it opens with the very sentence the toast used to carry.
  const joinMutation = useJoinAffiliateProgram({ source })

  const [acceptedTerms, setAcceptedTerms] = useState(false)
  // The button stays live and answers instead of sitting dead: a disabled
  // primary action never says what is missing. Derived rather than stored so
  // ticking the box clears the message and unticking it brings it back.
  const [hasAttempted, setHasAttempted] = useState(false)
  const showTermsError = hasAttempted && !acceptedTerms

  const hasTrackedView = useRef(false)
  const hasJoined = useRef(false)

  useEffect(() => {
    if (open && !hasTrackedView.current) {
      trackAffiliateActivationDialogViewed()
      hasTrackedView.current = true
    }
    if (!open) {
      hasTrackedView.current = false
      hasJoined.current = false
      // Acceptance belongs to the opening it was given in: reopening the
      // dialog has to ask for it again rather than inherit a stale tick.
      setAcceptedTerms(false)
      setHasAttempted(false)
    }
  }, [open])

  const handleJoin = () => {
    if (!acceptedTerms) {
      setHasAttempted(true)
      return
    }
    joinMutation.mutate(undefined, {
      onSuccess: () => {
        hasJoined.current = true
        onActivated()
      },
    })
  }

  // A join closes this dialog too, and that outcome is already
  // `Affiliate Program Joined` — only a close that left the account outside
  // the program is a dismissal.
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && open && !hasJoined.current) {
      trackAffiliateActivationDialogDismissed()
    }
    onOpenChange(nextOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {t('description')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogBody>
          <div className="space-y-4">
            <ul className="space-y-3">
              {HIGHLIGHTS.map((highlight) => (
                <li key={highlight.key} className="flex items-start gap-3">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${highlight.background}`}
                  >
                    <highlight.icon className={`h-5 w-5 ${highlight.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {t(`highlights.${highlight.key}.title`)}
                    </p>
                    <p className="text-muted-foreground mt-1.5 text-xs">
                      {t(`highlights.${highlight.key}.description`)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="space-y-1.5">
              <AffiliateTermsConsent
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                disabled={joinMutation.isPending}
              />
              {showTermsError && (
                <p role="alert" className="text-destructive text-sm">
                  {t('validation.terms')}
                </p>
              )}
            </div>
          </div>
        </ResponsiveDialogBody>

        <ResponsiveDialogFooter>
          {/* Closing mid-join would strand a request that is about to change what this dialog is for. */}
          <ResponsiveDialogDismiss
            intent="close"
            disabled={joinMutation.isPending}
          />
          <LoadingButton
            type="button"
            isLoading={joinMutation.isPending}
            onClick={handleJoin}
          >
            {t('actions.join')}
          </LoadingButton>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
